import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Pencil, Trash2, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

// ---- Types ----

interface TextAgent {
  id: string;
  name: string;
  channel: string;
  graphJsonb: string;
  isActive: boolean;
  updatedAt: string;
}

interface AgentFormState {
  name: string;
  channel: string;
  graphJsonb: string;
  isActive: boolean;
}

const CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP', 'WEBCHAT'] as const;

const DEFAULT_GRAPH = JSON.stringify(
  {
    nodes: [
      { id: 'n1', type: 'say', message: 'Hello! How can I help you today?' },
      { id: 'n2', type: 'wait_for_reply' },
      { id: 'n3', type: 'classify_intent', intents: ['INTERESTED', 'NOT_INTERESTED', 'QUESTION'] },
      { id: 'n4', type: 'branch', on: 'intent', cases: { INTERESTED: 'n5', NOT_INTERESTED: 'n6', default: 'n7' } },
      { id: 'n5', type: 'handoff_to_rep' },
      { id: 'n6', type: 'end' },
      { id: 'n7', type: 'say', message: 'Let me help with that question.' },
    ],
    start: 'n1',
  },
  null,
  2,
);

// ---- Component ----

export function TextAgentsPage() {
  const { t } = useTranslation();
  const [agents, setAgents] = useState<TextAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TextAgent | null>(null);
  const [form, setForm] = useState<AgentFormState>({
    name: '',
    channel: 'WEBCHAT',
    graphJsonb: DEFAULT_GRAPH,
    isActive: true,
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<TextAgent[]>('/text-agents');
      setAgents(data);
    } catch {
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function openCreate() {
    setEditing(null);
    setForm({ name: '', channel: 'WEBCHAT', graphJsonb: DEFAULT_GRAPH, isActive: true });
    setModalOpen(true);
  }

  function openEdit(agent: TextAgent) {
    setEditing(agent);
    setForm({
      name: agent.name,
      channel: agent.channel,
      graphJsonb: agent.graphJsonb,
      isActive: agent.isActive,
    });
    setModalOpen(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      if (editing) {
        await apiClient.put(`/text-agents/${editing.id}`, form);
        showToast(t('textAgents.updated'));
      } else {
        await apiClient.post('/text-agents', form);
        showToast(t('textAgents.created'));
      }
      setModalOpen(false);
      await load();
    } catch {
      showToast(t('textAgents.error_save'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(agent: TextAgent) {
    if (!window.confirm(t('textAgents.delete_confirm'))) return;
    try {
      await apiClient.delete(`/text-agents/${agent.id}`);
      showToast(t('textAgents.deleted'));
      await load();
    } catch {
      showToast(t('textAgents.error_delete'));
    }
  }

  return (
    <div className="space-y-4">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('textAgents.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('textAgents.subtitle')}</p>
        </div>
        <Button onClick={openCreate}>{t('textAgents.new_button')}</Button>
      </div>

      {/* Loading */}
      {loading && (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      )}

      {/* Empty state */}
      {!loading && agents.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Bot className="mb-3 h-12 w-12" />
          <p className="text-sm">{t('textAgents.empty')}</p>
        </div>
      )}

      {/* Table */}
      {!loading && agents.length > 0 && (
        <div className="rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">{t('textAgents.name')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('textAgents.channel')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('textAgents.status')}</th>
                <th className="px-4 py-3 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {agents.map((agent) => (
                <tr key={agent.id} className="border-b last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{agent.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {t(`textAgents.channel_${agent.channel}`)}
                  </td>
                  <td className="px-4 py-3">
                    {agent.isActive ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900/40 px-2 py-0.5 text-xs font-medium text-emerald-200">
                        <CheckCircle2 className="h-3 w-3" />
                        {t('textAgents.active')}
                      </span>
                    ) : (
                      <span className="inline-flex rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                        {t('textAgents.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(agent)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(agent)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 pt-16 px-4">
          <div className="w-full max-w-2xl rounded-xl border bg-card shadow-xl">
            <div className="border-b px-6 py-4">
              <h2 className="text-lg font-semibold">
                {editing ? t('textAgents.modal_edit_title') : t('textAgents.modal_create_title')}
              </h2>
            </div>
            <div className="space-y-4 px-6 py-4">
              {/* Name */}
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t('textAgents.field_name')}
                </label>
                <input
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>

              {/* Channel */}
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t('textAgents.field_channel')}
                </label>
                <select
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  value={form.channel}
                  onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
                >
                  {CHANNELS.map((ch) => (
                    <option key={ch} value={ch}>
                      {t(`textAgents.channel_${ch}`)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Graph DSL */}
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {t('textAgents.field_graph')}
                </label>
                <p className="mb-1 text-xs text-muted-foreground">{t('textAgents.graph_hint')}</p>
                <textarea
                  className="w-full rounded-md border bg-background px-3 py-2 font-mono text-xs"
                  rows={12}
                  value={form.graphJsonb}
                  onChange={(e) => setForm((f) => ({ ...f, graphJsonb: e.target.value }))}
                />
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-active"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                  className="h-4 w-4 rounded border"
                />
                <label htmlFor="is-active" className="text-sm">
                  {t('textAgents.field_active')}
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <Button variant="outline" onClick={() => setModalOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? t('common.loading') : t('textAgents.save')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
